// Remote support — one live session on THIS device, for either role (shared/REMOTE-SUPPORT.md §1, §4, §5).
// Glue between the plugin (through a `SupportTransport`: the web client's worker, or the supporthost's own
// bearer-token fetch), the peer connection (`SupportRtc`), the data-channel codec, the helper's input
// encoder and the requester's safety gate. No DOM: the viewer / the native app draw from `state`.
import type { CallIceServer } from '../core/callIceServer';
import { ControlGate } from './gate';
import { InputEncoder, realClock, type EncoderClock } from './inputEncoder';
import {
  decodeHelperMessage,
  decodeRequesterMessage,
  encode,
  PROTOCOL_VERSION,
  type HelperMessage,
  type NoticeCode,
  type RequesterMessage,
  type RequesterOs,
  type SignalPayload,
} from './protocol';
import { browserRtc, SupportRtc, type RtcEnvironment } from './rtc';

export interface SupportTransport {
  /** `POST /sessions/{id}/signal {payload}`. Rejects on failure (the outbox retries). */
  postSignal(sessionId: string, payload: SignalPayload): Promise<void>;
  /** `POST /sessions/{id}/active` (idempotent). */
  markActive(sessionId: string): Promise<void>;
  /** STUN/TURN (ice.ts `loadIceServers`). */
  iceServers(): Promise<CallIceServer[]>;
  log(line: string): void;
}

/**
 * Signals leave in the order they were produced, one POST at a time: two concurrent POSTs can overtake each
 * other, and a candidate that reaches the helper before its offer belongs to no connection. A failed POST is
 * retried (3×, 300 ms apart) before the queue moves on.
 */
export class SignalOutbox {
  private chain: Promise<void> = Promise.resolve();
  private stopped = false;

  constructor(
    private readonly post: (payload: SignalPayload) => Promise<void>,
    private readonly log: (line: string) => void,
    private readonly wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  ) {}

  push(payload: SignalPayload): Promise<void> {
    this.chain = this.chain.then(async () => {
      for (let attempt = 0; attempt < 3 && !this.stopped; attempt++) {
        try {
          await this.post(payload);
          return;
        } catch (error) {
          this.log(`signal ${payload.type} failed (${attempt + 1}/3): ${String(error)}`);
          await this.wait(300);
        }
      }
    });
    return this.chain;
  }

  stop(): void {
    this.stopped = true;
  }
}

export type SupportPhase = 'starting' | 'connecting' | 'connected' | 'ended';

export interface HelperViewState {
  phase: SupportPhase;
  /** RTCPeerConnection.connectionState ('' before the first). */
  link: string;
  /** The requester granted control (from `hello` / `control`). */
  control: boolean;
  remote: { os: RequesterOs; w: number; h: number; name: string } | null;
  stream: MediaStream | null;
  /** The release combo paused key forwarding (until the viewer is clicked). */
  keysPaused: boolean;
  /** «Pedir control» was sent and not answered yet. */
  asked: boolean;
  /** The requester's last `notice` (shown a few seconds; `noticeAt` = when, for the viewer's timer). */
  notice: NoticeCode | null;
  noticeAt: number;
  /** The requester said it cannot be controlled at all (`view_only_no_permission`, a Mac without Accessibility). */
  noControl: boolean;
  endedReason: string;
}

export interface HelperListener {
  state(state: HelperViewState): void;
  /** The session is over on this side (link lost / requester said bye): the owner posts `/end` and closes. */
  ended(reason: string): void;
}

/** The helper's half: answer, view, send input. */
export class HelperSession {
  private rtc: SupportRtc | null = null;
  private readonly outbox: SignalOutbox;
  readonly encoder: InputEncoder;
  private pending: SignalPayload[] = [];
  private current: HelperViewState = { phase: 'starting', link: '', control: false, remote: null, stream: null, keysPaused: false, asked: false, notice: null, noticeAt: 0, noControl: false, endedReason: '' };

  constructor(
    readonly sessionId: string,
    private readonly transport: SupportTransport,
    private readonly listener: HelperListener,
    private readonly env: RtcEnvironment = browserRtc,
    clock: EncoderClock = realClock,
  ) {
    this.outbox = new SignalOutbox((p) => transport.postSignal(sessionId, p), (l) => transport.log(l));
    this.encoder = new InputEncoder((m) => this.sendInput(m), clock);
  }

  get state(): HelperViewState {
    return this.current;
  }

  /** `reopen`: the session was already connecting/active before this viewer existed → ask for a fresh offer. */
  async start(reopen: boolean): Promise<void> {
    const ice = await this.transport.iceServers();
    if (this.current.phase === 'ended') return;
    this.rtc = new SupportRtc('helper', ice, {
      signal: (p) => void this.outbox.push(p),
      channelOpen: () => {
        this.transport.log('[remote] data channel open');
        this.update({ phase: 'connected' });
        void this.transport.markActive(this.sessionId).catch((e) => this.transport.log(`active failed: ${String(e)}`));
      },
      channelClosed: () => this.update({ phase: this.current.phase === 'ended' ? 'ended' : 'connecting' }),
      message: (text) => this.onMessage(text),
      remoteStream: (stream) => this.update({ stream }),
      connectionState: (link) => this.update({ link }),
      ended: (reason) => this.finish(reason),
      log: (line) => this.transport.log(line),
    }, this.env);
    this.update({ phase: 'connecting' });
    const queued = this.pending;
    this.pending = [];
    for (const p of queued) void this.rtc.handleSignal(p);
    if (reopen && !queued.some((p) => p.type === 'offer')) this.rtc.sendReady();
  }

  handleSignal(payload: SignalPayload): void {
    if (this.current.phase === 'ended') return;
    if (!this.rtc) this.pending.push(payload);
    else void this.rtc.handleSignal(payload);
  }

  /** «Pedir control». */
  askForControl(): void {
    if (this.current.control || this.current.noControl || !this.rtc?.channelOpen) return;
    this.encoder.askForControl();
    this.update({ asked: true });
  }

  /** The viewer was clicked after the release combo. */
  resumeKeys(): void {
    this.encoder.resumeKeys();
    this.update({ keysPaused: false });
  }

  /** The encoder reported the release combo. */
  keysReleased(): void {
    this.update({ keysPaused: true });
  }

  /** «Terminar» (or the session ended elsewhere). Idempotent. */
  stop(reason = 'helper'): void {
    if (this.current.phase === 'ended') return;
    this.encoder.dispose();
    this.outbox.stop();
    this.rtc?.close();
    this.update({ phase: 'ended', endedReason: reason, stream: null, control: false });
  }

  private finish(reason: string): void {
    if (this.current.phase === 'ended') return;
    this.transport.log(`[remote] ended: ${reason}`);
    this.stop(reason);
    this.listener.ended(reason);
  }

  private sendInput(message: HelperMessage): void {
    this.rtc?.send(encode(message));
  }

  private onMessage(text: string): void {
    const m = decodeRequesterMessage(text);
    if (!m) return;
    switch (m.t) {
      case 'hello':
        this.encoder.setGranted(m.control);
        this.update({ remote: { os: m.os, w: m.w, h: m.h, name: m.name }, control: m.control, asked: m.control ? false : this.current.asked });
        this.transport.log(`[remote] hello ${m.os} ${m.w}x${m.h} control=${m.control}`);
        return;
      case 'control':
        this.encoder.setGranted(m.granted);
        this.update({ control: m.granted, asked: false });
        this.transport.log(`[remote] control ${m.granted ? 'on' : 'off'}`);
        return;
      case 'bye':
        this.finish(m.reason ? `bye:${m.reason}` : 'bye');
        return;
      case 'notice':
        this.update(m.code === 'view_only_no_permission' ? { notice: m.code, noticeAt: Date.now(), noControl: true, asked: false } : { notice: m.code, noticeAt: Date.now() });
        return;
    }
  }

  private update(patch: Partial<HelperViewState>): void {
    this.current = { ...this.current, ...patch };
    this.listener.state(this.current);
  }
}

export interface RequesterViewState {
  phase: SupportPhase;
  link: string;
  control: boolean;
  endedReason: string;
}

export interface RequesterListener {
  state(state: RequesterViewState): void;
  /** A helper message that passed the gate: inject it. */
  input(message: HelperMessage): void;
  /** Lifts for keys/buttons the gate let down (control withdrawn / session over): inject them. */
  release(messages: HelperMessage[]): void;
  /** The helper asked for control (`want`): show the permission prompt again. */
  want(): void;
  ended(reason: string): void;
}

export interface SharedDisplay {
  os: RequesterOs;
  /** The shared display's pixel size (hello `w`/`h`). 0 = read it off the track. */
  w: number;
  h: number;
  name: string;
}

/** The requester's half: offer the screen, gate and forward the helper's input. */
export class RequesterSession {
  private rtc: SupportRtc | null = null;
  private readonly outbox: SignalOutbox;
  readonly gate = new ControlGate();
  private screen: { track: MediaStreamTrack; stream: MediaStream } | null = null;
  private display: SharedDisplay = { os: 'windows', w: 0, h: 0, name: '' };
  private pending: SignalPayload[] = [];
  private current: RequesterViewState = { phase: 'starting', link: '', control: false, endedReason: '' };
  /** Counters for the log / tests: what the gate did. Never the input content (§5). */
  readonly counts: Record<string, number> = {};

  constructor(
    readonly sessionId: string,
    private readonly transport: SupportTransport,
    private readonly listener: RequesterListener,
    private readonly env: RtcEnvironment = browserRtc,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.outbox = new SignalOutbox((p) => transport.postSignal(sessionId, p), (l) => transport.log(l));
  }

  get state(): RequesterViewState {
    return this.current;
  }

  async start(stream: MediaStream, display: SharedDisplay): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('no video track');
    this.screen = { track, stream };
    this.display = display;
    track.addEventListener('ended', () => this.finish('screenStopped'));
    const ice = await this.transport.iceServers();
    if (this.current.phase === 'ended') return;
    this.rtc = new SupportRtc('requester', ice, {
      signal: (p) => void this.outbox.push(p),
      channelOpen: () => this.onOpen(),
      channelClosed: () => this.update({ phase: this.current.phase === 'ended' ? 'ended' : 'connecting' }),
      message: (text) => this.onMessage(text),
      remoteStream: () => undefined,
      connectionState: (link) => this.update({ link }),
      ended: (reason) => this.finish(reason),
      log: (line) => this.transport.log(line),
    }, this.env);
    this.update({ phase: 'connecting' });
    // Signals that came before the screen was picked: an answer to nothing is moot, a `ready` is satisfied
    // by the offer below, candidates belong to no connection yet.
    this.pending = [];
    await this.rtc.offer(track, stream);
  }

  handleSignal(payload: SignalPayload): void {
    if (this.current.phase === 'ended') return;
    if (!this.rtc) this.pending.push(payload);
    else void this.rtc.handleSignal(payload);
  }

  /** «Permitir control» / «Quitar control» / «Dar control». */
  setControl(granted: boolean): void {
    const lifts = this.gate.setGranted(granted);
    if (lifts.length) this.listener.release(lifts);
    if (this.current.control !== granted) this.transport.log(`[remote] control ${granted ? 'on' : 'off'}`);
    this.update({ control: granted });
    this.rtc?.send(encode({ t: 'control', granted }));
  }

  private lastNoticeAt = -Infinity;

  /** The injector could not reach the active window (elevated): tell the helper, at most once per 5 s. */
  notice(code: NoticeCode): void {
    const now = this.now();
    if (now - this.lastNoticeAt < 5_000) return;
    if (this.rtc?.send(encode({ t: 'notice', code }))) this.lastNoticeAt = now;
  }

  /** The requester used the physical mouse/keyboard: remote input pauses 1 s. */
  localInput(): void {
    this.gate.noteLocalInput(this.now());
  }

  /** «Terminar» / emergency stop / the session ended elsewhere. Sends `bye` first when it can. */
  stop(reason = 'requester'): void {
    if (this.current.phase === 'ended') return;
    const lifts = this.gate.releaseAll();
    if (lifts.length) this.listener.release(lifts);
    this.rtc?.send(encode({ t: 'bye', reason } satisfies RequesterMessage));
    this.outbox.stop();
    const rtc = this.rtc;
    // Let the bye leave before the channel closes under it.
    setTimeout(() => rtc?.close(), 150);
    this.screen?.track.stop();
    this.update({ phase: 'ended', endedReason: reason, control: false });
  }

  private finish(reason: string): void {
    if (this.current.phase === 'ended') return;
    this.transport.log(`[remote] ended: ${reason}`);
    this.stop(reason);
    this.listener.ended(reason);
  }

  private onOpen(): void {
    const settings = this.screen?.track.getSettings?.() ?? {};
    const w = this.display.w || settings.width || 0;
    const h = this.display.h || settings.height || 0;
    this.rtc?.send(encode({ t: 'hello', v: PROTOCOL_VERSION, os: this.display.os, w, h, name: this.display.name, control: this.gate.isGranted }));
    this.transport.log(`[remote] data channel open; hello ${w}x${h}`);
    this.update({ phase: 'connected' });
    void this.transport.markActive(this.sessionId).catch((e) => this.transport.log(`active failed: ${String(e)}`));
  }

  private onMessage(text: string): void {
    const m = decodeHelperMessage(text);
    if (!m) {
      this.count('malformed');
      return;
    }
    const verdict = this.gate.check(m, this.now());
    this.count(verdict);
    if (verdict === 'pass') this.listener.input(m);
    else if (verdict === 'want') this.listener.want();
  }

  private count(key: string): void {
    this.counts[key] = (this.counts[key] ?? 0) + 1;
  }

  private update(patch: Partial<RequesterViewState>): void {
    this.current = { ...this.current, ...patch };
    this.listener.state(this.current);
  }
}
